<script setup lang="ts">
import { useDeviceActions } from '@/stores/DeviceActions';
import { ASSIGN_DEVICE, type Device } from '@/graphql/devices';
import { useEnvironments } from '@/composables/environments';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useToast } from 'bootstrap-vue-next';
import { useMutation } from '@urql/vue';
import VSelect, { type SelectOption } from '@stl/components/VSelect.vue';

const toast = useToast();
const editForm = ref<HTMLFormElement | null>(null);

const { currentDevice, isEditDeviceVisible } = storeToRefs(useDeviceActions());
const editMode = computed(() => !!currentDevice.value?.environmentId);
const error = ref('');
const wasValidated = ref(false);

const title = computed(() => (editMode.value ? 'Edit Device' : 'Assign Device'));

const assignInput = reactive({
  registrationId: 0,
  environmentId: '',
  name: ''
});

const { environments } = useEnvironments();
const environmentOptions = computed<SelectOption[]>(() => {
  return environments.value.map((env) => ({ value: env.id, title: env.description }));
});

function open() {
  assignInput.registrationId = currentDevice.value?.id || 0;
  assignInput.environmentId = currentDevice.value?.environmentId || '';
  assignInput.name = currentDevice.value?.name || '';
}

function close() {
  error.value = '';
  wasValidated.value = false;
}

const { executeMutation, fetching } = useMutation<{ assignDevice: Device }>(ASSIGN_DEVICE);

function save() {
  error.value = '';
  let isValid = editForm.value?.checkValidity();
  if (assignInput.environmentId === '') {
    isValid = false;
    error.value = 'Environment must be selected';
  }
  wasValidated.value = true;
  if (!isValid) {
    return;
  }
  executeMutation({
    input: assignInput
  }).then((result) => {
    if (result.error || !result.data?.assignDevice) {
      error.value =
        result.error?.message || `Failed to ${editMode.value ? 'edit' : 'assign'} device for unknown reason`;
      return;
    }
    toast.show?.({
      props: { variant: 'success', body: `Successfully ${editMode.value ? 'edited' : 'assigned'} device` }
    });
    isEditDeviceVisible.value = false;
  });
}
</script>

<template lang="pug">
BModal#registerDevice(v-model="isEditDeviceVisible" :title="title" no-close-on-backdrop no-close-on-esc @show="open" @hide="close")
  AlertBox(v-if="error" variant="danger") {{ error }}
  template(v-if="currentDevice")
    form.needs-validation(ref="editForm" :class="{ 'was-validated': wasValidated }" no-validate @submit.prevent="false")
      .has-validation.mb-3
        .mb-3
          label.form-label(for="edit-device-serial") Serial Number
          input#edit-device-serial.form-control(ref="serialNumber" v-model="currentDevice.serialNumber" type="text" disabled)
        .has-validation.mb-3
          label.form-label(for="edit-device-name") Name
          input#edit-device-name.form-control(ref="name" v-model="assignInput.name" type="text" placeholder="Name")
        .has-validation.mb-3
          label.form-label(for="edit-device-environment") Environment
          VSelect(v-model="assignInput.environmentId" :options="environmentOptions" placeholder="Environment" :disabled="editMode" input-id="edit-device-environment")
  template(#footer)
    button.btn.btn-link(@click="isEditDeviceVisible = false") Cancel
    LaddaButton.btn.btn-primary(:is-loading="fetching" @click="save") {{ title }}
</template>
